import { getConfig } from '@/config'

/**
 * Sets CSS custom properties for theme colors
 */
export const setThemeColors = (): void => {
  const config = getConfig()
  const root = document.documentElement

  // Set primary color and its variations
  const primaryColor = config.theme.colors.primary
  root.style.setProperty('--tw-primary', primaryColor)
  root.style.setProperty('--tw-primary-dark', config.theme.dark?.primary || primaryColor)
  root.style.setProperty('--tw-primary-accent', config.theme.colors.accent || primaryColor)

  // Generate primary color variations
  const primaryVariations = generateColorVariations(primaryColor)
  Object.entries(primaryVariations).forEach(([shade, color]) => {
    root.style.setProperty(`--tw-primary-${shade}`, color)
  })

  // Set secondary color
  root.style.setProperty('--tw-secondary', config.theme.colors.secondary)
  root.style.setProperty('--tw-secondary-dark', config.theme.dark?.secondary || config.theme.colors.secondary)

  // Set accent color
  root.style.setProperty('--tw-accent', config.theme.colors.accent)
  root.style.setProperty('--tw-accent-dark', config.theme.dark?.accent || config.theme.colors.accent)

  // Set background colors
  root.style.setProperty('--tw-app-bg', config.theme.colors.background || '#F3F4F6')
  root.style.setProperty('--tw-app-bg-dark', config.theme.dark?.background || '#111827')
}

/**
 * Generates color variations for a given color
 */
export const generateColorVariations = (color: string): Record<string, string> => {
  // Convert hex to RGB
  const hex = color.replace('#', '')
  const r = parseInt(hex.substr(0, 2), 16)
  const g = parseInt(hex.substr(2, 2), 16)
  const b = parseInt(hex.substr(4, 2), 16)

  const variations: Record<string, string> = {}

  // Generate variations from 50 to 900
  const shades = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900]
  
  shades.forEach((shade) => {
    let factor: number
    
    if (shade <= 100) {
      factor = 1 + (100 - shade) / 100 * 0.9 // Lighter shades
    } else if (shade <= 500) {
      factor = 1 - (shade - 100) / 400 * 0.3 // Medium shades
    } else {
      factor = 1 - (shade - 500) / 400 * 0.7 // Darker shades
    }

    const newR = Math.round(Math.min(255, Math.max(0, r * factor)))
    const newG = Math.round(Math.min(255, Math.max(0, g * factor)))
    const newB = Math.round(Math.min(255, Math.max(0, b * factor)))

    variations[shade] = `rgb(${newR}, ${newG}, ${newB})`
  })

  return variations
}

/**
 * Initialize theme colors on app startup
 */
export const initializeTheme = (): void => {
  setThemeColors()
}
